// Import van ledenlijsten. Puur: geen DOM, geen database. Krijgt de rijen uit
// een Excel- of CSV-bestand (eerste rij = kolomkoppen) en levert per rij een
// persoon, ouders, lidmaatschap en een lijst fouten en waarschuwingen.
//
// Uitgangspunt: liever een foutmelding dan een gok. Een datum die niet
// eenduidig te lezen is, wordt niet ingevuld maar gemeld. Wat een lijst in de
// praktijk bevat staat in docs/ledenlaag-toets.md; de testset staat in
// tests/fixtures/ledenlijsten en tests/import.mjs.
//
// Meldingen zijn codes met waarden, geen tekst: de pagina vertaalt ze via
// i18n (sleutels import.melding.<code>).

// ── Doelvelden ─────────────────────────────────────────────────────────────
export const DOELEN = [
  'negeren', 'volledige_naam', 'voornaam', 'tussenvoegsel', 'achternaam',
  'geboortedatum', 'geslacht', 'email', 'telefoon', 'adres', 'straat',
  'huisnummer', 'postcode', 'plaats', 'land', 'nationaliteiten', 'lidnummer',
  'trainingsgroep', 'graad_opgave', 'notities', 'lid_sinds', 'ouder_naam',
  'ouder1_email', 'ouder2_email', 'club',
];

// Kolommen die we nooit importeren, ook niet als iemand ze kiest.
export const GEWEIGERD = ['paspoort', 'gezin'];

const plat = s => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');

const SYNONIEMEN = [
  ['paspoort', ['paspoort', 'passport', 'paspoortnummer', 'passportnumber', 'idnummer', 'documentnummer']],
  ['gezin', ['gezin', 'familie', 'family', 'household']],
  ['ouder1_email', ['ouder1email', 'ouder1emailadres', 'parent1email', 'emailouder1', 'emailouder']],
  ['ouder2_email', ['ouder2email', 'ouder2emailadres', 'parent2email', 'emailouder2']],
  ['ouder_naam', ['naamouder', 'ouder', 'ouders', 'parent', 'parentname', 'contactpersoon', 'verzorger']],
  ['volledige_naam', ['naam', 'name', 'volledigenaam', 'fullname', 'lid', 'member']],
  ['voornaam', ['voornaam', 'firstname', 'roepnaam', 'givenname']],
  ['tussenvoegsel', ['tussenvoegsel', 'tussenv', 'tv', 'prefix', 'voorvoegsel']],
  ['achternaam', ['achternaam', 'lastname', 'surname', 'familienaam']],
  ['geboortedatum', ['geboortedatum', 'gebdatum', 'gebdat', 'geboren', 'dateofbirth', 'dob', 'birthdate', 'birthday']],
  ['geslacht', ['geslacht', 'gender', 'sex', 'mv']],
  ['email', ['email', 'emailadres', 'mail', 'emailaddress']],
  ['telefoon', ['telefoon', 'tel', 'telefoonnummer', 'mobiel', 'gsm', 'phone', 'mobile']],
  ['adres', ['adres', 'address', 'woonadres']],
  ['straat', ['straat', 'street', 'straatnaam']],
  ['huisnummer', ['huisnummer', 'huisnr', 'nr', 'number', 'housenumber']],
  ['postcode', ['postcode', 'zip', 'zipcode', 'postalcode']],
  ['plaats', ['plaats', 'woonplaats', 'city', 'town', 'stad']],
  ['land', ['land', 'country']],
  ['nationaliteiten', ['nationaliteit', 'nationaliteiten', 'nationality', 'nationalities']],
  ['lidnummer', ['lidnummer', 'lidnr', 'membernumber', 'memberno', 'memberid', 'relatienummer']],
  ['trainingsgroep', ['groep', 'trainingsgroep', 'group', 'lesgroep', 'klas']],
  ['graad_opgave', ['graad', 'band', 'band', 'kup', 'grade', 'belt', 'rang']],
  ['notities', ['opmerkingen', 'opmerking', 'notities', 'notitie', 'notes', 'remarks', 'bijzonderheden']],
  ['lid_sinds', ['lidsinds', 'membersince', 'startdatum', 'inschrijfdatum', 'datumlid', 'sinds']],
  // Alleen bij een bond: de club van het lid, gekoppeld via src/lib/club-match.js.
  ['club', ['club', 'clubnaam', 'vereniging', 'verenigingsnaam', 'sportschool', 'dojang', 'school']],
];

// Stelt per kolomkop een doelveld voor. Onbekend wordt 'negeren'; een kolom
// die we nooit importeren, krijgt de code uit GEWEIGERD zodat de pagina hem
// kan tonen maar niet kan laten kiezen.
export function stelDoelVoor(kop) {
  const k = plat(kop);
  if (!k) return 'negeren';
  if (/contributie|bijdrage|fee|bedrag|iban|rekening/.test(k)) return 'negeren';
  if (/paspoort|passport/.test(k)) return 'paspoort';
  if (/^leden|gezinsleden/.test(k)) return 'gezin';
  for (const [doel, woorden] of SYNONIEMEN) if (woorden.includes(k)) return doel;
  for (const [doel, woorden] of SYNONIEMEN) if (woorden.some(w => w.length > 3 && k.startsWith(w))) return doel;
  return 'negeren';
}

export function stelMappingVoor(koppen, rijen = []) {
  const gebruikt = new Set();
  return koppen.map((kop, index) => {
    let doel = stelDoelVoor(kop);
    // Twee kolommen op hetzelfde veld: de tweede wordt niet automatisch gekozen.
    if (doel !== 'negeren' && !GEWEIGERD.includes(doel) && gebruikt.has(doel)) doel = 'negeren';
    gebruikt.add(doel);
    const voorbeeld = rijen.map(r => r[index]).filter(v => v !== null && v !== undefined && String(v).trim() !== '')
      .slice(0, 3).map(v => (v instanceof Date ? isoDatum(v) : String(v)));
    return { index, kop: String(kop ?? ''), doel, voorbeeld };
  });
}

// ── Waarden ────────────────────────────────────────────────────────────────
const MAANDEN = {
  jan: 1, januari: 1, january: 1, feb: 2, februari: 2, february: 2, mrt: 3, maart: 3, mar: 3, march: 3,
  apr: 4, april: 4, mei: 5, may: 5, jun: 6, juni: 6, june: 6, jul: 7, juli: 7, july: 7,
  aug: 8, augustus: 8, august: 8, sep: 9, sept: 9, september: 9, okt: 10, oct: 10, oktober: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};

const pad = n => String(n).padStart(2, '0');
function isoDatum(d) { return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`; }

function geldig(j, m, d) {
  const dt = new Date(Date.UTC(j, m - 1, d));
  return dt.getUTCFullYear() === j && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

// Geeft { waarde: 'jjjj-mm-dd' | null, fout: code | null }. Dag eerst, zoals in
// Nederland; een datum als 03-04-2012 is dus 3 april.
export function parseDatum(v, { vandaag = new Date() } = {}) {
  if (v === null || v === undefined || String(v).trim() === '') return { waarde: null, fout: null };
  let j, m, d;
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    j = v.getUTCFullYear(); m = v.getUTCMonth() + 1; d = v.getUTCDate();
  } else if (typeof v === 'number' && v > 1 && v < 80000) {
    // Excel-serienummer (dagen sinds 30 december 1899)
    const dt = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86400000);
    j = dt.getUTCFullYear(); m = dt.getUTCMonth() + 1; d = dt.getUTCDate();
  } else {
    const s = String(v).trim().toLowerCase().replace(/\s+/g, ' ');
    let r;
    if ((r = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:t.*)?$/))) { j = +r[1]; m = +r[2]; d = +r[3]; }
    else if ((r = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/))) {
      d = +r[1]; m = +r[2]; j = +r[3];
      if (r[3].length === 2) return { waarde: null, fout: 'datum_tweecijferig' };
    }
    else if ((r = s.match(/^(\d{1,2}) ([a-z]+)\.? (\d{4})$/)) && MAANDEN[r[2]]) { d = +r[1]; m = MAANDEN[r[2]]; j = +r[3]; }
    else return { waarde: null, fout: 'datum_onleesbaar' };
  }
  if (!geldig(j, m, d)) return { waarde: null, fout: 'datum_ongeldig' };
  const iso = `${j}-${pad(m)}-${pad(d)}`;
  if (j < 1900 || iso > isoDatum(vandaag)) return { waarde: null, fout: 'datum_buiten_bereik' };
  return { waarde: iso, fout: null };
}

// Lid sinds is vaak alleen een jaar of een maand. Die worden de eerste dag.
export function parseLidSinds(v) {
  if (v === null || v === undefined || String(v).trim() === '') return { waarde: null, fout: null };
  const s = String(v).trim().toLowerCase();
  let r;
  if ((r = s.match(/^(\d{4})$/))) return { waarde: `${r[1]}-01-01`, fout: null };
  if ((r = s.match(/^([a-z]+)\.? (\d{4})$/)) && MAANDEN[r[1]]) return { waarde: `${r[2]}-${pad(MAANDEN[r[1]])}-01`, fout: null };
  const d = parseDatum(v);
  return d.fout ? { waarde: null, fout: 'lidsinds_onleesbaar' } : d;
}

export function parseGeslacht(v) {
  const k = plat(v);
  if (!k) return { waarde: null, fout: null };
  if (['m', 'man', 'male', 'jongen', 'boy', 'heer', 'dhr', 'mannelijk'].includes(k)) return { waarde: 'm', fout: null };
  if (['v', 'f', 'vrouw', 'female', 'meisje', 'girl', 'mevr', 'vrouwelijk'].includes(k)) return { waarde: 'v', fout: null };
  if (['x', 'anders', 'other', 'nonbinair', 'nonbinary', 'overig'].includes(k)) return { waarde: 'x', fout: null };
  return { waarde: null, fout: 'geslacht_onbekend' };
}

const LANDEN = {
  NL: ['nl', 'ned', 'nederland', 'nederlands', 'nederlandse', 'dutch', 'holland', 'netherlands'],
  BE: ['be', 'bel', 'belg', 'belgie', 'belgisch', 'belgische', 'belgian', 'belgium'],
  DE: ['de', 'ger', 'duits', 'duitse', 'duitsland', 'german', 'germany'],
  PL: ['pl', 'pol', 'pools', 'poolse', 'polen', 'polish', 'poland'],
  FR: ['fr', 'fra', 'frans', 'franse', 'frankrijk', 'french', 'france'],
  GB: ['gb', 'uk', 'brits', 'britse', 'british', 'engeland', 'english', 'unitedkingdom'],
  ES: ['es', 'esp', 'spaans', 'spaanse', 'spanje', 'spanish', 'spain'],
  IT: ['it', 'ita', 'italiaans', 'italiaanse', 'italie', 'italian', 'italy'],
  MA: ['ma', 'mar', 'marokkaans', 'marokkaanse', 'marokko', 'moroccan', 'morocco'],
  TR: ['tr', 'tur', 'turks', 'turkse', 'turkije', 'turkish', 'turkey'],
  SE: ['se', 'swe', 'zweeds', 'zweedse', 'zweden', 'swedish', 'sweden'],
  NO: ['no', 'nor', 'noors', 'noorse', 'noorwegen', 'norwegian', 'norway'],
  IE: ['ie', 'irl', 'iers', 'ierse', 'ierland', 'irish', 'ireland'],
};

export function parseNationaliteiten(v) {
  const s = String(v ?? '').trim();
  if (!s) return { waarde: [], fout: null };
  const uit = []; let fout = null;
  for (const deel of s.split(/\s*(?:\/|,|;|\+|&|\ben\b|\band\b)\s*/i)) {
    const k = plat(deel);
    if (!k) continue;
    const code = Object.keys(LANDEN).find(c => LANDEN[c].includes(k))
      || (/^[a-z]{2}$/.test(k) ? k.toUpperCase() : null);
    if (code) { if (!uit.includes(code)) uit.push(code); } else fout = 'nationaliteit_onbekend';
  }
  return { waarde: uit, fout };
}

const TUSSENVOEGSELS = ['van der', 'van den', 'van de', "van 't", "in 't", 'in het', 'van', 'de', 'der', 'den', 'het', "'t", 'ter', 'te', 'ten', 'von', 'la', 'le', 'du', 'da', 'di', 'op de', 'uit de', 'uit het'];

function splitsTussenvoegsel(achternaam) {
  const s = achternaam.trim();
  const laag = s.toLowerCase();
  for (const tv of TUSSENVOEGSELS.sort((a, b) => b.length - a.length)) {
    if (laag.startsWith(tv + ' ') && s.length > tv.length + 1) {
      return { tussenvoegsel: s.slice(0, tv.length).toLowerCase(), achternaam: s.slice(tv.length + 1).trim() };
    }
  }
  return { tussenvoegsel: null, achternaam: s };
}

// "Jansen, Piet", "de Vries, J.", "Mo El Amrani", "Piet de Vries".
export function splitsNaam(v) {
  const s = String(v ?? '').trim().replace(/\s+/g, ' ');
  if (!s) return { fout: 'naam_leeg' };
  if (s.includes(',')) {
    const [achter, ...rest] = s.split(',');
    const voor = rest.join(',').trim();
    return { voornaam: voor || null, ...splitsTussenvoegsel(achter) };
  }
  const woorden = s.split(' ');
  if (woorden.length === 1) return { voornaam: null, tussenvoegsel: null, achternaam: s };
  const voornaam = woorden[0];
  return { voornaam, ...splitsTussenvoegsel(woorden.slice(1).join(' ')) };
}

// "Dorpsstraat 12a, 1234 AB Ergenshuizen", "Kerkweg 7 1234EF Ergenshuizen",
// "Stationsplein 10-2". Wat niet te herkennen is, gaat in straat.
export function splitsAdres(v) {
  const s = String(v ?? '').trim().replace(/\s+/g, ' ');
  if (!s) return {};
  let r = s.match(/^(.*?\D)\s+(\d+[\w\- ]{0,6}?)\s*,?\s*(\d{4}\s?[a-zA-Z]{2})\s+(.+)$/);
  if (r) return { straat: r[1].trim(), huisnummer: r[2].trim(), postcode: normPostcode(r[3]), plaats: r[4].trim() };
  r = s.match(/^(.*?\D)\s+(\d+[\w\-]*(?:\s?[a-zA-Z])?)$/);
  if (r) return { straat: r[1].trim(), huisnummer: r[2].trim() };
  return { straat: s };
}

export function normPostcode(v) {
  const s = String(v ?? '').trim();
  const r = s.match(/^(\d{4})\s?([a-zA-Z]{2})$/);
  return r ? `${r[1]} ${r[2].toUpperCase()}` : (s || null);
}

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/i;
export function parseEmail(v) {
  const s = String(v ?? '').trim();
  if (!s) return { waarde: null, fout: null };
  return EMAIL.test(s) ? { waarde: s.toLowerCase(), fout: null } : { waarde: null, fout: 'email_ongeldig' };
}

// "Fatima El Amrani (moeder)" wordt naam plus soort.
function parseOuderNaam(v) {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const r = s.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  const naam = (r ? r[1] : s).trim();
  const extra = plat(r?.[2] || '');
  const kind = /voogd|guardian/.test(extra) ? 'voogd' : /verzorg|carer/.test(extra) ? 'verzorger' : 'ouder';
  return naam ? { naam, kind } : null;
}

const tekst = v => {
  if (v === null || v === undefined) return null;
  const s = (v instanceof Date ? isoDatum(v) : String(v)).trim();
  return s || null;
};

// ── Rijen verwerken ────────────────────────────────────────────────────────
// mapping: [{ index, doel }]. bestaand: personen die al in de organisatie
// staan ({ achternaam, geboortedatum, email, voornaam }), voor de controle op
// dubbelen.
export function verwerk(rijen, mapping, { bestaand = [], vandaag = new Date() } = {}) {
  const kolom = {};
  for (const m of mapping) if (m.doel !== 'negeren' && !GEWEIGERD.includes(m.doel)) kolom[m.doel] = m.index;
  const waarde = (rij, doel) => (kolom[doel] === undefined ? null : rij[kolom[doel]]);

  const sleutels = new Map();
  const sleutel = p => [
    p.achternaam && p.geboortedatum ? `n:${p.achternaam.toLowerCase()}|${p.geboortedatum}` : null,
    p.email && p.voornaam ? `e:${p.email.toLowerCase()}|${p.voornaam.toLowerCase()}` : null,
  ].filter(Boolean);
  for (const b of bestaand) for (const k of sleutel(b)) sleutels.set(k, 'bestaand');

  // Een kolom met meerdere gezinsleden in één cel is niet betrouwbaar te
  // splitsen. Dan geldt voor elke rij: één regel per persoon aanleveren.
  const perGezin = mapping.some(m => m.doel === 'gezin');

  const uitkomst = [];
  rijen.forEach((rij, i) => {
    if (!rij || rij.every(c => c === null || c === undefined || String(c).trim() === '')) return;
    const fouten = []; const waarschuwingen = [];
    const p = {};
    if (perGezin) fouten.push({ code: 'gezinsregel' });

    // Naam
    if (kolom.volledige_naam !== undefined && waarde(rij, 'volledige_naam')) {
      const ruw = String(waarde(rij, 'volledige_naam'));
      if (!perGezin && (/\(\d{1,3}\)\s*[,;]/.test(ruw) || /:\s*\S+\s*\(/.test(ruw))) fouten.push({ code: 'gezinsregel' });
      Object.assign(p, splitsNaam(ruw));
      delete p.fout;
    }
    for (const veld of ['voornaam', 'tussenvoegsel', 'achternaam']) {
      if (kolom[veld] !== undefined && tekst(waarde(rij, veld))) p[veld] = tekst(waarde(rij, veld));
    }
    if (p.tussenvoegsel) p.tussenvoegsel = p.tussenvoegsel.toLowerCase();
    if (!p.achternaam) fouten.push({ code: 'achternaam_ontbreekt' });

    // Datums en keuzes
    if (kolom.geboortedatum !== undefined) {
      const d = parseDatum(waarde(rij, 'geboortedatum'), { vandaag });
      if (d.fout) waarschuwingen.push({ code: d.fout, waarden: { waarde: tekst(waarde(rij, 'geboortedatum')) } });
      p.geboortedatum = d.waarde;
    }
    if (kolom.geslacht !== undefined) {
      const g = parseGeslacht(waarde(rij, 'geslacht'));
      if (g.fout) waarschuwingen.push({ code: g.fout, waarden: { waarde: tekst(waarde(rij, 'geslacht')) } });
      p.geslacht = g.waarde;
    }
    if (kolom.nationaliteiten !== undefined) {
      const n = parseNationaliteiten(waarde(rij, 'nationaliteiten'));
      if (n.fout) waarschuwingen.push({ code: n.fout, waarden: { waarde: tekst(waarde(rij, 'nationaliteiten')) } });
      p.nationaliteiten = n.waarde;
    }
    if (kolom.email !== undefined) {
      const e = parseEmail(waarde(rij, 'email'));
      if (e.fout) waarschuwingen.push({ code: e.fout, waarden: { waarde: tekst(waarde(rij, 'email')) } });
      p.email = e.waarde;
    }

    // Adres
    if (kolom.adres !== undefined) Object.assign(p, splitsAdres(waarde(rij, 'adres')));
    for (const veld of ['straat', 'huisnummer', 'plaats', 'land', 'telefoon', 'lidnummer', 'trainingsgroep', 'graad_opgave', 'notities']) {
      if (kolom[veld] !== undefined && tekst(waarde(rij, veld))) p[veld] = tekst(waarde(rij, veld));
    }
    if (kolom.postcode !== undefined && tekst(waarde(rij, 'postcode'))) p.postcode = normPostcode(waarde(rij, 'postcode'));
    else if (p.postcode) p.postcode = normPostcode(p.postcode);
    if (p.land) {
      const l = parseNationaliteiten(p.land);
      p.land = l.waarde[0] || p.land;
    }

    // Lidmaatschap
    let lidSinds = null;
    if (kolom.lid_sinds !== undefined) {
      const l = parseLidSinds(waarde(rij, 'lid_sinds'));
      if (l.fout) waarschuwingen.push({ code: l.fout, waarden: { waarde: tekst(waarde(rij, 'lid_sinds')) } });
      lidSinds = l.waarde;
    }

    // Ouders
    const ouders = [];
    const ouderNaam = parseOuderNaam(waarde(rij, 'ouder_naam'));
    for (const [i2, doel] of [[0, 'ouder1_email'], [1, 'ouder2_email']]) {
      const e = parseEmail(waarde(rij, doel));
      if (e.fout) waarschuwingen.push({ code: e.fout, waarden: { waarde: tekst(waarde(rij, doel)) } });
      if (e.waarde) ouders.push({ email: e.waarde, naam: i2 === 0 ? ouderNaam?.naam || null : null, kind: i2 === 0 ? ouderNaam?.kind || 'ouder' : 'ouder' });
    }
    if (ouderNaam && !ouders.length) ouders.push({ email: null, naam: ouderNaam.naam, kind: ouderNaam.kind });

    // Dubbelen
    let status = fouten.length ? 'fout' : 'nieuw';
    let dubbelVan = null;
    for (const k of sleutel(p)) {
      if (sleutels.has(k)) { dubbelVan = sleutels.get(k); break; }
    }
    if (status === 'nieuw' && dubbelVan === 'bestaand') { status = 'overslaan'; waarschuwingen.push({ code: 'bestaat_al' }); }
    else if (status === 'nieuw' && dubbelVan !== null) { status = 'overslaan'; waarschuwingen.push({ code: 'dubbel_in_bestand', waarden: { rij: dubbelVan } }); }
    if (status === 'nieuw') for (const k of sleutel(p)) sleutels.set(k, i + 2);

    uitkomst.push({ rij: i + 2, persoon: p, ouders, lidSinds, club: tekst(waarde(rij, 'club')), fouten, waarschuwingen, status });
  });

  const tel = s => uitkomst.filter(u => u.status === s).length;
  const geweigerd = mapping.filter(m => GEWEIGERD.includes(m.doel)).map(m => ({ kop: m.kop, doel: m.doel }));
  return {
    personen: uitkomst,
    samenvatting: { nieuw: tel('nieuw'), overslaan: tel('overslaan'), fout: tel('fout'), geweigerd },
  };
}

// Eenvoudige CSV-lezer: puntkomma of komma, aanhalingstekens volgens RFC 4180.
export function leesCsv(tekstInhoud) {
  const s = tekstInhoud.replace(/^﻿/, '');
  // Eerste regel met inhoud: sommige exports (Sportdata) beginnen met een lege regel.
  const eersteRegel = s.split(/\r?\n/).find(r => r.trim() !== '') || '';
  const scheiding = (eersteRegel.match(/;/g) || []).length > (eersteRegel.match(/,/g) || []).length ? ';' : ',';
  const rijen = []; let rij = []; let veld = ''; let inQuotes = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"' && s[i + 1] === '"') { veld += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else veld += c;
    } else if (c === '"') inQuotes = true;
    else if (c === scheiding) { rij.push(veld); veld = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      rij.push(veld); rijen.push(rij); rij = []; veld = '';
    } else veld += c;
  }
  if (veld !== '' || rij.length) { rij.push(veld); rijen.push(rij); }
  return rijen.filter(r => r.some(c => c.trim() !== ''));
}
